import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { Search, Image as ImageIcon, Film, Grid, List, FolderOpen, ChevronRight, CalendarDays } from 'lucide-react';
import { useTheme } from '../../context/ThemeContext';
import { authenticatedFetch } from '../../utils/api';
import { API_HOST as CONST_API_HOST } from '../../utils/constants';
import { apiPost } from '../../utils/apiAdapter';
import ResourceTree from './ResourceTree';
import ResourceCard from './ResourceCard';
import BatchDownloadBar from './BatchDownloadBar';
import StudioArtwork from '../ui/StudioArtwork';
import '../ui/StudioGlass.css';
import './ResourceCenter.css';
import {
  formatMediaDuration,
  getResourceThumbnailUrl,
  groupResourcesByDate,
  isVideoResource,
  sortResourcesByDateDesc
} from './resourceViewUtils';

const RESOURCE_PAGE_SIZE = 80;
const RESOURCE_DISCOVERY_POLL_INTERVAL = 10_000;
const EMPTY_PAGE_STATE = {
  hasMore: false,
  nextCursorCreatedAt: null,
  nextCursorId: null,
  nextCursorUndated: false
};

const getPageCursorKey = (cursor) => cursor
  ? `${cursor.nextCursorCreatedAt || 'undated'}:${cursor.nextCursorId ?? 'start'}:${cursor.nextCursorUndated ? 1 : 0}`
  : 'first-page';

const ResourceListThumbnail = ({ resource }) => {
  const thumbnailUrl = getResourceThumbnailUrl(resource);
  const isVideo = isVideoResource(resource);
  const [imageFailed, setImageFailed] = useState(false);

  useEffect(() => {
    setImageFailed(false);
  }, [thumbnailUrl]);

  if (!thumbnailUrl || imageFailed) {
    return (
      <div
        className="studio-resource-thumbnail relative flex h-12 w-12 shrink-0 items-center justify-center overflow-hidden rounded"
        style={{ backgroundColor: 'var(--studio-surface, var(--color-bg-overlay))' }}
      >
        {isVideo
          ? <Film size={16} style={{ color: 'var(--color-text-muted)' }} />
          : <ImageIcon size={16} style={{ color: 'var(--color-text-muted)' }} />}
      </div>
    );
  }

  return (
    <div
      className="studio-resource-thumbnail relative h-12 w-12 shrink-0 overflow-hidden rounded"
      style={{ backgroundColor: 'var(--studio-surface, var(--color-bg-overlay))' }}
    >
      <img
        src={thumbnailUrl}
        alt=""
        className="h-full w-full object-cover"
        loading="lazy"
        decoding="async"
        draggable={false}
        onError={() => setImageFailed(true)}
      />
      {isVideo && (
        <div
          className="absolute inset-0 flex items-center justify-center"
          style={{ backgroundColor: 'var(--overlay-light)' }}
        >
          <Film size={12} className="text-white" />
        </div>
      )}
    </div>
  );
};

const ResourceDateHeading = ({ id, label, count }) => (
  <div
    className="studio-resource-date sticky top-0 z-20 -mx-4 mb-4 flex items-center gap-2.5 px-4 py-2"
    style={{
      backgroundColor: 'color-mix(in srgb, var(--studio-surface, var(--color-bg-secondary)) 92%, transparent)',
      backdropFilter: 'blur(10px)'
    }}
  >
    <CalendarDays size={22} style={{ color: 'var(--color-text-muted)' }} />
    <h2 id={id} className="text-2xl font-semibold" style={{ color: 'var(--color-text-primary)' }}>
      {label}
    </h2>
    <span
      className="rounded-full px-2 py-0.5 text-xs font-medium"
      style={{
        color: 'var(--color-text-secondary)',
        backgroundColor: 'var(--studio-raised, var(--color-bg-overlay))'
      }}
    >
      {count} 项
    </span>
  </div>
);

/**
 * 生成资源中心 - 主容器组件
 */
const ResourceCenter = ({ projectId, onOpenWorkstation }) => {
  const { getTheme } = useTheme();
  const theme = getTheme();

  // 树形数据
  const [treeData, setTreeData] = useState(null);
  const [treeLoading, setTreeLoading] = useState(false);

  // 资源列表
  const [resources, setResources] = useState([]);
  const [resourcesLoading, setResourcesLoading] = useState(false);

  // 选中状态
  const [selectedIds, setSelectedIds] = useState([]);
  const [activeNode, setActiveNode] = useState(null);

  // 筛选
  const [typeFilter, setTypeFilter] = useState('all');
  const [searchKeyword, setSearchKeyword] = useState('');
  const [debouncedSearchKeyword, setDebouncedSearchKeyword] = useState('');
  const [viewMode, setViewMode] = useState('grid');

  // 服务端游标分页，避免项目资源量大时一次性返回全部内容。
  const [pageState, setPageState] = useState(EMPTY_PAGE_STATE);
  const [resourceStats, setResourceStats] = useState({ total: 0, images: 0, videos: 0 });
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const scrollRef = useRef(null);
  const thumbnailPollAttemptsRef = useRef(new Map());
  const resourcePageCursorByIdRef = useRef(new Map());
  const resourcesRequestIdRef = useRef(0);
  const loadMoreInFlightRef = useRef(false);

  useEffect(() => {
    const timer = window.setTimeout(() => {
      setDebouncedSearchKeyword(searchKeyword.trim());
    }, 300);
    return () => window.clearTimeout(timer);
  }, [searchKeyword]);

  const loadTree = useCallback(async () => {
    if (!projectId) return;
    setTreeLoading(true);
    try {
      const data = await authenticatedFetch(`${CONST_API_HOST}/api/resource/tree?projectId=${projectId}`);
      setTreeData(data);
    } catch (err) {
      console.error('加载资源树失败:', err);
    } finally {
      setTreeLoading(false);
    }
  }, [projectId]);

  // 加载资源列表
  const loadResources = useCallback(async (
    node,
    { silent = false, append = false, cursor = null } = {}
  ) => {
    if (!projectId) return;
    if (append && loadMoreInFlightRef.current) return;

    // 静默封面刷新只读取当前查询版本，不应让正在进行的翻页请求失效。
    const requestId = silent
      ? resourcesRequestIdRef.current
      : ++resourcesRequestIdRef.current;
    if (append) {
      loadMoreInFlightRef.current = true;
      setIsLoadingMore(true);
    } else if (!silent) {
      thumbnailPollAttemptsRef.current = new Map();
      resourcePageCursorByIdRef.current = new Map();
      setResourcesLoading(true);
      setPageState(EMPTY_PAGE_STATE);
      if (scrollRef.current) scrollRef.current.scrollTop = 0;
    }

    try {
      const payload = {
        projectId: parseInt(projectId),
        limit: RESOURCE_PAGE_SIZE,
        mediaType: typeFilter,
        keyword: debouncedSearchKeyword || null,
        includeCounts: !append && !silent
      };

      if (node) {
        if (node.type === 'episode') {
          payload.episodeId = node.episodeId;
        } else if (node.type === 'shot') {
          payload.episodeId = node.episodeId;
          payload.shotNumber = node.shotNumber;
        } else if (node.type === 'asset_category') {
          payload.source = node.category === 'characters' ? 'character'
            : node.category === 'scenes' ? 'scene'
            : node.category === 'props' ? 'prop' : null;
        } else if (node.type === 'ai_source') {
          payload.source = node.sourceDetail;
        } else if (node.type === 'archived') {
          payload.status = 'archived';
        }
      }

      if (cursor) {
        payload.cursorCreatedAt = cursor.nextCursorCreatedAt;
        payload.cursorId = cursor.nextCursorId;
        payload.cursorUndated = cursor.nextCursorUndated;
      }

      const data = await apiPost(`${CONST_API_HOST}/api/resource/query-page`, payload);
      if (requestId !== resourcesRequestIdRef.current) return;

      const items = Array.isArray(data?.items) ? data.items : [];
      const uniqueItems = Array.from(new Map(items.map(item => [String(item.id), item])).values());
      const pageCursor = cursor ? { ...cursor } : null;
      uniqueItems.forEach(item => {
        resourcePageCursorByIdRef.current.set(String(item.id), pageCursor);
      });

      if (silent) {
        setResources((previous) => {
          const incomingById = new Map(uniqueItems.map(item => [String(item.id), item]));
          const previousIds = new Set(previous.map(item => String(item.id)));
          const refreshed = previous.map(item => incomingById.get(String(item.id)) || item);
          const newItems = uniqueItems.filter(item => !previousIds.has(String(item.id)));
          return sortResourcesByDateDesc([...newItems, ...refreshed]);
        });
      } else if (append) {
        setResources((previous) => {
          const merged = new Map(previous.map(item => [String(item.id), item]));
          uniqueItems.forEach(item => merged.set(String(item.id), item));
          return sortResourcesByDateDesc(Array.from(merged.values()));
        });
      } else {
        setResources(sortResourcesByDateDesc(uniqueItems));
      }

      if (!silent) {
        setPageState({
          hasMore: Boolean(data?.hasMore),
          nextCursorCreatedAt: data?.nextCursorCreatedAt || null,
          nextCursorId: data?.nextCursorId ?? null,
          nextCursorUndated: Boolean(data?.nextCursorUndated)
        });
      }

      if (!append && !silent) {
        setResourceStats({
          total: Number(data?.totalCount ?? uniqueItems.length),
          images: Number(data?.imageCount ?? uniqueItems.filter(r => !isVideoResource(r)).length),
          videos: Number(data?.videoCount ?? uniqueItems.filter(isVideoResource).length)
        });
      }
    } catch (err) {
      console.error('加载资源失败:', err);
      if (!silent && !append && requestId === resourcesRequestIdRef.current) {
        setResources([]);
        setResourceStats({ total: 0, images: 0, videos: 0 });
        setPageState(EMPTY_PAGE_STATE);
      }
    } finally {
      if (append) {
        loadMoreInFlightRef.current = false;
        setIsLoadingMore(false);
      } else if (requestId === resourcesRequestIdRef.current) {
        setResourcesLoading(false);
      }
    }
  }, [debouncedSearchKeyword, projectId, typeFilter]);

  // 项目切换时重置目录选择，资源查询由下方统一 effect 触发。
  useEffect(() => {
    setActiveNode(null);
    setSelectedIds([]);
    loadTree();
  }, [loadTree, projectId]);

  useEffect(() => {
    setSelectedIds([]);
    loadResources(activeNode);
  }, [activeNode, loadResources]);

  // Generation completes outside this component. Refresh the first page while
  // visible so newly persisted resources appear without a browser refresh.
  useEffect(() => {
    if (!projectId) return undefined;
    const refreshVisibleResources = () => {
      if (document.visibilityState !== 'visible' || resourcesLoading || isLoadingMore) return;
      loadResources(activeNode, { silent: true });
    };
    const timer = window.setInterval(refreshVisibleResources, RESOURCE_DISCOVERY_POLL_INTERVAL);
    window.addEventListener('focus', refreshVisibleResources);
    return () => {
      window.clearInterval(timer);
      window.removeEventListener('focus', refreshVisibleResources);
    };
  }, [activeNode, isLoadingMore, loadResources, projectId, resourcesLoading]);

  const handleScroll = useCallback((event) => {
    const element = event.currentTarget;
    const isNearBottom = element.scrollHeight - element.scrollTop - element.clientHeight < 360;
    if (!isNearBottom || !pageState.hasMore || resourcesLoading || isLoadingMore) return;
    loadResources(activeNode, { append: true, cursor: pageState });
  }, [activeNode, isLoadingMore, loadResources, pageState, resourcesLoading]);

  // 后端会异步补齐视频封面；短时静默刷新，让首轮生成完成后直接出现在当前页面。
  useEffect(() => {
    const pendingResource = resources.find(resource => {
      if (!isVideoResource(resource) || getResourceThumbnailUrl(resource)) return false;
      const cursor = resourcePageCursorByIdRef.current.get(String(resource.id)) || null;
      return (thumbnailPollAttemptsRef.current.get(getPageCursorKey(cursor)) || 0) < 3;
    });
    if (!pendingResource || resourcesLoading || isLoadingMore) return undefined;

    const cursor = resourcePageCursorByIdRef.current.get(String(pendingResource.id)) || null;
    const cursorKey = getPageCursorKey(cursor);
    const timer = window.setTimeout(() => {
      const attempts = thumbnailPollAttemptsRef.current.get(cursorKey) || 0;
      thumbnailPollAttemptsRef.current.set(cursorKey, attempts + 1);
      loadResources(activeNode, { silent: true, cursor });
    }, 5000);
    return () => window.clearTimeout(timer);
  }, [activeNode, isLoadingMore, loadResources, resources, resourcesLoading]);

  // 树节点点击
  const handleNodeClick = (node) => {
    // 如果是分类节点（episodes / assets），不加载资源
    if (node.type === 'category') {
      setActiveNode(null);
      return;
    }
    setActiveNode(node);
    setSelectedIds([]);
  };

  // 服务端已经完成搜索、媒体类型和分镜筛选；这里只保留一层分镜兜底。
  const filteredResources = useMemo(() => {
    if (activeNode?.type !== 'shot' || activeNode.shotNumber == null) return resources;
    return resources.filter(r => r.shotNumber === activeNode.shotNumber);
  }, [resources, activeNode]);

  const sortedFilteredResources = useMemo(
    () => sortResourcesByDateDesc(filteredResources),
    [filteredResources]
  );

  const groupedResources = useMemo(
    () => groupResourcesByDate(sortedFilteredResources),
    [sortedFilteredResources]
  );

  const groupedVisibleResources = useMemo(
    () => groupedResources.map(group => ({ ...group, totalCount: group.resources.length })),
    [groupedResources]
  );

  const stats = resourceStats;
  const activeResultTotal = typeFilter === 'image'
    ? stats.images
    : typeFilter === 'video' ? stats.videos : stats.total;

  // 多选
  const toggleSelect = (id) => {
    setSelectedIds(prev =>
      prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]
    );
  };

  // 面包屑
  const breadcrumbs = useMemo(() => {
    const crumbs = [{ label: '全部资源', node: null }];
    if (!activeNode) return crumbs;

    if (activeNode.type === 'episode') {
      const ep = treeData?.episodes?.find(e => e.episodeId === activeNode.episodeId);
      crumbs.push({ label: ep?.episodeTitle || `集数 ${activeNode.episodeId}`, node: activeNode });
    } else if (activeNode.type === 'shot') {
      const ep = treeData?.episodes?.find(e => e.episodeId === activeNode.episodeId);
      crumbs.push({
        label: ep?.episodeTitle || `集数 ${activeNode.episodeId}`,
        node: { type: 'episode', episodeId: activeNode.episodeId, id: `episode_${activeNode.episodeId}` }
      });
      crumbs.push({ label: `分镜 #${String(activeNode.shotNumber).padStart(3, '0')}`, node: activeNode });
    } else if (activeNode.type === 'asset_category') {
      const labels = { characters: '角色素材', scenes: '场景素材', props: '道具素材' };
      crumbs.push({ label: labels[activeNode.category] || activeNode.category, node: activeNode });
    } else if (activeNode.type === 'archived') {
      crumbs.push({ label: '废案', node: activeNode });
    }

    return crumbs;
  }, [activeNode, treeData]);

  // 预览
  const handlePreview = (resource) => {
    const isVideo = isVideoResource(resource);

    if (isVideo) {
      // 视频：打开视频预览弹窗
      setPreviewVideo(resource);
    } else {
      // 图片：打开 ImageWorkstation
      if (onOpenWorkstation) {
        onOpenWorkstation(resource.url || resource.thumbnailUrl);
      }
    }
  };

  // 视频预览状态
  const [previewVideo, setPreviewVideo] = useState(null);

  return (
    <div className="studio-resource-center flex h-full w-full overflow-hidden rounded-xl"
      style={{ backgroundColor: 'var(--studio-surface, var(--color-bg-secondary))' }}>

      {/* 左侧树形导航 */}
      <div className="studio-resource-sidebar w-56 shrink-0 flex flex-col"
        style={{ borderRight: '1px solid var(--studio-line, var(--color-border-primary))' }}>
        <div className="studio-resource-sidebar__heading px-3 py-2.5 shrink-0"
          style={{ borderBottom: '1px solid var(--studio-line, var(--color-border-primary))' }}>
          <h3 className="text-sm font-semibold flex items-center gap-2"
            style={{ color: 'var(--color-text-primary)' }}>
            <FolderOpen size={14} style={{ color: theme.colors.accentPrimary }} />
            资源导航
          </h3>
        </div>
        <div className="flex-1 overflow-hidden">
          {treeLoading ? (
            <div className="flex items-center justify-center h-full text-xs"
              style={{ color: 'var(--color-text-muted)' }}>加载中...</div>
          ) : (
            <ResourceTree
              treeData={treeData}
              activeNode={activeNode}
              onNodeClick={handleNodeClick}
            />
          )}
        </div>
      </div>

      {/* 右侧内容区 */}
      <div className="studio-resource-main flex-1 min-w-0 flex flex-col overflow-hidden">

        {/* 面包屑 + 筛选栏 */}
        <div className="studio-resource-toolbar px-4 py-2.5 space-y-3 shrink-0"
          style={{ borderBottom: '1px solid var(--studio-line, var(--color-border-primary))' }}>

          {/* 面包屑 */}
          <nav className="flex flex-wrap items-center gap-1 text-xs" aria-label="资源路径">
            {breadcrumbs.map((crumb, i) => (
              <React.Fragment key={i}>
                {i > 0 && <ChevronRight size={12} style={{ color: 'var(--color-text-muted)' }} />}
                <button
                  className="studio-resource-breadcrumb"
                  aria-current={i === breadcrumbs.length - 1 ? 'page' : undefined}
                  style={{
                    color: i === breadcrumbs.length - 1 ? 'var(--color-text-primary)' : 'var(--color-text-muted)',
                    fontWeight: i === breadcrumbs.length - 1 ? 600 : 400
                  }}
                  onClick={() => handleNodeClick(crumb.node || { type: 'category', id: 'all' })}
                >
                  {crumb.label}
                </button>
              </React.Fragment>
            ))}
          </nav>

          {/* 筛选行 */}
          <div className="studio-resource-filters flex flex-wrap items-center gap-3">
            {/* 类型筛选 */}
            <div className="studio-resource-types flex items-center gap-1" role="group" aria-label="资源类型">
              {[
                { id: 'all', label: '全部', icon: Grid },
                { id: 'image', label: '图片', icon: ImageIcon },
                { id: 'video', label: '视频', icon: Film }
              ].map(opt => (
                <button key={opt.id}
                  onClick={() => setTypeFilter(opt.id)}
                  className="studio-resource-type flex items-center gap-1.5 px-3 h-8 rounded-md text-xs font-medium"
                  aria-pressed={typeFilter === opt.id}
                  style={{
                    backgroundColor: typeFilter === opt.id ? 'var(--studio-selected-fill, var(--color-bg-tertiary))' : 'transparent',
                    color: typeFilter === opt.id ? theme.colors.accentPrimary : 'var(--studio-muted, var(--color-text-muted))',
                  }}>
                  <opt.icon size={13} />{opt.label}
                </button>
              ))}
            </div>

            {/* 搜索 */}
            <div className="studio-resource-search relative flex-1 max-w-xs">
              <Search size={13} className="absolute left-2.5 top-1/2 -translate-y-1/2"
                style={{ color: 'var(--color-text-muted)' }} />
              <input
                type="text"
                value={searchKeyword}
                onChange={(e) => setSearchKeyword(e.target.value)}
                placeholder="搜索提示词/描述..."
                aria-label="搜索资源提示词或描述"
                className="w-full h-9 pl-8 pr-3 rounded-lg text-xs outline-none"
                style={{
                  backgroundColor: 'var(--color-bg-primary)',
                  border: '1px solid var(--studio-line, var(--color-border-primary))',
                  color: 'var(--color-text-primary)'
                }}
              />
            </div>

            <div className="studio-resource-spacer flex-1" />

            {/* 统计 */}
            <span className="studio-resource-count text-xs font-mono-num" style={{ color: 'var(--studio-muted, var(--color-text-muted))' }}>
              共 {stats.total} 个资源 ({stats.images} 图片 · {stats.videos} 视频)
            </span>

            {/* 视图切换 */}
            <div className="studio-resource-views flex items-center gap-0.5 p-0.5 rounded-lg" role="group" aria-label="资源视图"
              style={{ backgroundColor: 'var(--color-bg-primary)' }}>
              <button onClick={() => setViewMode('grid')} className="h-8 w-8 flex items-center justify-center rounded-md"
                aria-pressed={viewMode === 'grid'}
                aria-label="网格视图"
                title="网格视图"
                style={{
                  color: viewMode === 'grid' ? theme.colors.accentPrimary : 'var(--color-text-muted)',
                  backgroundColor: viewMode === 'grid' ? 'var(--studio-selected-fill, var(--color-bg-overlay))' : 'transparent'
                }}>
                <Grid size={13} />
              </button>
              <button onClick={() => setViewMode('list')} className="h-8 w-8 flex items-center justify-center rounded-md"
                aria-pressed={viewMode === 'list'}
                aria-label="列表视图"
                title="列表视图"
                style={{
                  color: viewMode === 'list' ? theme.colors.accentPrimary : 'var(--color-text-muted)',
                  backgroundColor: viewMode === 'list' ? 'var(--studio-selected-fill, var(--color-bg-overlay))' : 'transparent'
                }}>
                <List size={13} />
              </button>
            </div>
          </div>
        </div>

        {/* 资源网格 */}
        <div className="flex-1 overflow-y-auto custom-scrollbar" ref={scrollRef} onScroll={handleScroll}>
          {resourcesLoading ? (
            <div className="flex items-center justify-center h-full p-4 text-xs"
              style={{ color: 'var(--color-text-muted)' }}>加载中...</div>
          ) : filteredResources.length === 0 ? (
            <div className="studio-resource-empty flex flex-col items-center justify-center h-full p-4"
              style={{ color: 'var(--color-text-muted)' }}>
              <StudioArtwork variant="empty" className="studio-resource-empty__art" />
              <p className="mt-2 text-sm font-semibold" style={{ color: 'var(--color-text-secondary)' }}>暂无资源</p>
              <p className="mt-1 text-xs">生成图片或视频后，资源会自动出现在这里</p>
            </div>
          ) : viewMode === 'grid' ? (
            <div className="px-4 pb-4">
            <div>
              {groupedVisibleResources.map(group => (
                <section key={group.key} className="pb-7 last:pb-0" aria-labelledby={`resource-date-${group.key}`}>
                  <ResourceDateHeading
                    id={`resource-date-${group.key}`}
                    label={group.label}
                    count={group.totalCount}
                  />
                  <div className="studio-resource-grid grid gap-3">
                    {group.resources.map(resource => (
                      <ResourceCard
                        key={resource.id}
                        resource={resource}
                        isSelected={selectedIds.includes(resource.id)}
                        onToggleSelect={toggleSelect}
                        onPreview={handlePreview}
                      />
                    ))}
                  </div>
                </section>
              ))}
            </div>
            {(pageState.hasMore || isLoadingMore) && (
              <div className="flex justify-center py-4">
                <span className="text-xs" style={{ color: 'var(--color-text-muted)' }}>
                  {isLoadingMore
                    ? '正在加载更多...'
                    : `已加载 ${resources.length} / ${activeResultTotal}，向下滚动加载更多`}
                </span>
              </div>
            )}
            </div>
          ) : (
            <div className="px-4 pb-4">
            <div>
              {groupedVisibleResources.map(group => (
                <section key={group.key} className="pb-7 last:pb-0" aria-labelledby={`resource-list-date-${group.key}`}>
                  <ResourceDateHeading
                    id={`resource-list-date-${group.key}`}
                    label={group.label}
                    count={group.totalCount}
                  />
                  <div className="space-y-2">
                    {group.resources.map(resource => {
                      const isSelected = selectedIds.includes(resource.id);
                      const isVideo = isVideoResource(resource);
                      const durationLabel = formatMediaDuration(resource.duration);
                      return (
                        <div key={resource.id}
                          className="studio-resource-row flex items-center gap-3 p-2 rounded-lg"
                          data-selected={isSelected ? 'true' : 'false'}
                          style={{
                            backgroundColor: isSelected ? 'var(--studio-selected-fill, var(--color-bg-overlay))' : 'var(--studio-surface, var(--color-bg-primary))',
                            border: `1px solid ${isSelected ? theme.colors.accentPrimary : 'var(--studio-line, var(--color-border-primary))'}`
                          }}>
                          <button type="button" className="studio-resource-row__select w-5 h-5 shrink-0 rounded border-2 flex items-center justify-center cursor-pointer"
                            aria-label={`${isSelected ? '取消选择' : '选择'}：${resource.name || '资源'}`}
                            aria-pressed={isSelected}
                            style={{
                              backgroundColor: isSelected ? theme.colors.accentPrimary : 'transparent',
                              borderColor: isSelected ? theme.colors.accentPrimary : 'var(--studio-line-strong, var(--color-border-primary))'
                            }}
                            onClick={(e) => { e.stopPropagation(); toggleSelect(resource.id); }}>
                            {isSelected && <svg width="12" height="12" viewBox="0 0 16 16" fill="none" aria-hidden="true"><path d="m3 8 3 3 7-7" stroke="var(--color-accent-contrast)" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" /></svg>}
                          </button>
                          <button type="button" className="studio-resource-row__preview flex min-w-0 flex-1 items-center gap-3 text-left"
                            aria-label={`预览${isVideo ? '视频' : '图片'}：${resource.name || '资源'}`}
                            onClick={() => handlePreview(resource)}>
                          <ResourceListThumbnail resource={resource} />
                          <div className="flex-1 min-w-0">
                            <div className="text-xs font-medium truncate" style={{ color: 'var(--color-text-primary)' }}>
                              {resource.name}
                            </div>
                            <div className="text-2xs truncate" style={{ color: 'var(--color-text-muted)' }}>
                              {resource.sourceLabel} · {resource.typeLabel}
                              {resource.shotNumber && ` · #${String(resource.shotNumber).padStart(3, '0')}`}
                              {durationLabel && ` · ${durationLabel}`}
                            </div>
                          </div>
                          {isVideo && <Film size={14} style={{ color: 'var(--color-text-muted)' }} />}
                          </button>
                        </div>
                      );
                    })}
                  </div>
                </section>
              ))}
            </div>
            {(pageState.hasMore || isLoadingMore) && (
              <div className="flex justify-center py-4">
                <span className="text-xs" style={{ color: 'var(--color-text-muted)' }}>
                  {isLoadingMore
                    ? '正在加载更多...'
                    : `已加载 ${resources.length} / ${activeResultTotal}，向下滚动加载更多`}
                </span>
              </div>
            )}
            </div>
          )}
        </div>

        {/* 批量操作栏 */}
        <BatchDownloadBar
          selectedIds={selectedIds}
          totalCount={stats.total}
          activeNode={activeNode}
          projectId={projectId}
          onClearSelection={() => setSelectedIds([])}
        />
      </div>

      {/* 视频预览弹窗 */}
      {previewVideo && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center animate-in fade-in duration-200"
          style={{ backgroundColor: 'var(--overlay-heavy)' }}
          onClick={() => setPreviewVideo(null)}
        >
          <div
            className="relative max-w-4xl max-h-[80vh] rounded-xl overflow-hidden animate-in fade-in zoom-in-95 duration-200"
            onClick={(e) => e.stopPropagation()}
          >
            <video
              src={previewVideo.url}
              className="w-full h-full"
              style={{ maxHeight: '80vh', objectFit: 'contain' }}
              controls
              autoPlay
              loop
            />
            <button
              onClick={() => setPreviewVideo(null)}
              className="absolute top-4 right-4 w-10 h-10 rounded-full flex items-center justify-center text-white transition-colors hover:bg-[var(--color-bg-tertiary)]"
              style={{ backgroundColor: 'var(--overlay-medium)' }}
            >
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <line x1="18" y1="6" x2="6" y2="18" />
                <line x1="6" y1="6" x2="18" y2="18" />
              </svg>
            </button>
            {previewVideo.prompt && (
              <div className="absolute bottom-0 left-0 right-0 px-4 py-3" style={{ backgroundColor: 'var(--overlay-heavy)' }}>
                <p className="text-sm text-white line-clamp-2">{previewVideo.prompt}</p>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default ResourceCenter;
